import CryptoJS from 'crypto-js';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const combineOrders = orders => {
  const result = {};
  orders.forEach(order => {
    if (!result[order.order]) {
      result[order.order] = { withSubscription: {}, withoutSubscription: {} };
    }
    const target =
      order.subscription !== null
        ? result[order.order].withSubscription
        : result[order.order].withoutSubscription;

    if (!target[order.product]) {
      target[order.product] = {
        quantity: 0,
        public_id: null,
        total_price: 0,
        subscription: order.subscription
      };
    }

    target[order.product].quantity += order.quantity;
    target[order.product].public_id = order.public_id;
    target[order.product].total_price += order.total_price;
    target[order.product].subscription = order.subscription;
  });

  const orderId = Object.keys(result)[0];
  const productsArray = [];

  if (orderId) {
    const processProducts = products => {
      return Object.keys(products).map(productId => ({
        id: productId,
        quantity: products[productId].quantity,
        public_id: products[productId].public_id,
        total_price: products[productId].total_price,
        subscription: products[productId].subscription
      }));
    };
    productsArray.push({
      subscription_type: 'subscription',
      products: processProducts(result[orderId].withSubscription)
    });

    productsArray.push({
      subscription_type: 'oneTime',
      products: processProducts(result[orderId].withoutSubscription)
    });
  }

  return {
    order: orderId,
    products: productsArray
  };
};

const generateOGAuthorization = customerId => {
  const STOREFRONT_API_KEY = process.env.GATSBY_ORDERGROOVE_KEY;
  const MERCHANT_ID = process.env.GATSBY_ORDERGROOVE_MERCHANT_ID;
  const ts = Math.floor(new Date().getTime() / 1000);
  const hash = CryptoJS.HmacSHA256(`${customerId}|${ts}`, STOREFRONT_API_KEY);
  const sig = CryptoJS.enc.Base64.stringify(hash);
  return JSON.stringify({
    public_id: MERCHANT_ID,
    sig_field: customerId,
    ts,
    sig
  });
};

export const getSubscriptionItems = async ({ customerId }) => {
  const url =
    'https://restapi.ordergroove.com/items/?page_size=100&status=6&status=1&ordering=order__place';
  const headers = {
    'Content-Type': 'application/json',
    Authorization: generateOGAuthorization(customerId)
  };
  try {
    const result = await axios.get(url, { headers: headers });
    if (result.data.results.length > 0) {
      const data = result.data.results;
      const orderItems = combineOrders(data);
      return orderItems;
    }
    return null;
  } catch (error) {
    console.error('API Error:', error); // Log the error
    return null;
  }
};


export const getOrders = async customerId => {
  const url = `https://restapi.ordergroove.com/orders/?page_size=10&status=6&status=1&ordering=place`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: generateOGAuthorization(customerId)
  };
  try {
    const result = await axios.get(url, { headers: headers });
    return result.data?.results;
  } catch (error) {
    console.error('API Error:', error); // Log the error
    return null;
  }
};

export const createItemToNextOG = async (orderOGId, itemDetails, customerId) => {
  try {
    const response = await axios.post(
      `https://restapi.ordergroove.com/items/iu/`,
      {
        order: orderOGId,
        product: itemDetails.productId,
        quantity: itemDetails.quantity,
        offer: '5150f692035011ee884a5e558a0a3596'
      },
      {
        headers: {
          Authorization: generateOGAuthorization(customerId),
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      'Error adding product to next order:',
      error.response ? error.response.data : error.message
    );
    return;
  }
};

export const createSubcription = async ({
  orderOGId,
  itemDetails,
  customerId,
  merchant,
  payment,
  shipping_address,
  every
}) => {
  try {
    const randomUUID = uuidv4();
    const response = await axios.post(
      `https://restapi.ordergroove.com/subscriptions/iu/`,
      {
        merchant: merchant,
        customer: customerId,
        offer: '5150f692035011ee884a5e558a0a3596',
        session_id: randomUUID,
        payment: payment,
        shipping_address: shipping_address,
        every: every,
        every_period: 2,
        extra_data: {},
        order: orderOGId,
        product: itemDetails.productId,
        quantity: itemDetails.quantity
      },
      {
        headers: {
          Authorization: generateOGAuthorization(customerId),
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error(
      'Error adding product to next subscription:',
      error.response ? error.response.data : error.message
    );
    return;
  }
};

export const getSubcription = async ({ productOGId, customerId, selectedOGDate }) => {
  try {
    const response = await axios.get(
      `https://restapi.ordergroove.com/subscriptions/?product=${productOGId}&live=true`,
      {
        headers: {
          Authorization: generateOGAuthorization(customerId),
          'Content-Type': 'application/json'
        }
      }
    );
    if (response.data?.results) {
      const currentSub = response.data?.results.find(
        i => i.every === Number(selectedOGDate) && i.every_period === 2
      );
      return currentSub || null;
    }
    return;
  } catch (error) {
    console.error('Error get subscription:', error.response ? error.response.data : error.message);
    return;
  }
};

export const changeSubQty = async (itemDetails, customerId) => {
  try {
    const response = await axios.put(
      `https://restapi.ordergroove.com/subscriptions/${itemDetails.id}/change_quantity/`,
      {
        quantity: itemDetails.quantity
      },
      {
        headers: {
          Authorization: generateOGAuthorization(customerId),
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error change qty:', error.response ? error.response.data : error.message);
    return;
  }
};

export const cancelSubscription = async (subscriptionId, customerId) => {
  try {
    const response = await axios.put(
      `https://restapi.ordergroove.com/subscriptions/${subscriptionId}/cancel/`,
      {},
      {
        headers: {
          Authorization: generateOGAuthorization(customerId),
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error change qty:', error.response ? error.response.data : error.message);
    return;
  }
};

export const changeItemQty = async (itemDetails, customerId) => {
  try {
    const response = await axios.put(
      `https://restapi.ordergroove.com/items/${itemDetails.id}/change_quantity/`,
      {
        quantity: itemDetails.quantity
      },
      {
        headers: {
          Authorization: generateOGAuthorization(customerId),
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error change qty:', error.response ? error.response.data : error.message);
    return;
  }
};

export const deleteItem = async (itemId, customerId) => {
  try {
    const response = await axios.delete(`https://restapi.ordergroove.com/items/${itemId}/delete/`, {
      headers: {
        Authorization: generateOGAuthorization(customerId),
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error delete item:', error.response ? error.response.data : error.message);
    return;
  }
};

export const addItemToNextOG = async ({
  currentItem = null,
  customerId,
  orderOGId = null,
  itemDetails = null
}) => {
  if (currentItem) {
    const itemData = {
      id: currentItem.public_id,
      quantity: currentItem.quantity + 1
    };
    await changeItemQty(itemData, customerId);
  } else {
    await createItemToNextOG(orderOGId, itemDetails, customerId);
  }
};

export const closestItem = nextOrders => {
  return nextOrders.reduce((closest, current) => {
    const currentDate = new Date();
    const currentPlaceDate = new Date(current.place);
    const closestPlaceDate = new Date(closest.place);
    return Math.abs(currentPlaceDate - currentDate) < Math.abs(closestPlaceDate - currentDate)
      ? current
      : closest;
  });
};
